/**
 * Compare Table Component
 *
 * Side-by-side comparison table for multiple funds.
 * Displays ratings, scores, metrics, and highlights best values.
 */

import { Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Star, TrendingUp, TrendingDown } from "lucide-react";
import type { Fund, FundRating } from "@curie/shared";

import { useBestValues } from "./useBestValues";
import {
  CATEGORY_LABELS,
  RATING_CONFIG,
  COMPARISON_METRICS,
  calculateCurieScore,
  getScoreColor,
} from "./compare-utils";

// ============================================================================
// Types
// ============================================================================

interface CompareTableProps {
  funds: Fund[];
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Rating display with stars
 */
function RatingDisplay({ rating }: { rating: FundRating }) {
  const config = RATING_CONFIG[rating];
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex gap-0.5">
        {Array.from({ length: config.stars }).map((_, i) => (
          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        ))}
        {Array.from({ length: 5 - config.stars }).map((_, i) => (
          <Star key={i} className="h-4 w-4 text-gray-300" />
        ))}
      </div>
      <span className={`text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    </div>
  );
}

/**
 * Score display component with color coding
 */
function ScoreDisplay({ score }: { score: number }) {
  return (
    <div className="text-center">
      <span className={`text-sm font-semibold ${getScoreColor(score)}`}>
        {score}
      </span>
      <span className="text-xs text-muted-foreground ml-1">/100</span>
    </div>
  );
}

/**
 * Get bar color classes based on score value
 * 80-100: emerald/green
 * 60-79: sky/blue
 * 40-59: amber/yellow
 * 0-39: rose/red
 */
function getBarColorClass(score: number): string {
  if (score >= 80) return "bg-emerald-500";
  if (score >= 60) return "bg-sky-500";
  if (score >= 40) return "bg-amber-500";
  return "bg-rose-500";
}

/**
 * Inline progress bar for score visualization
 */
function ScoreBar({ score }: { score: number }) {
  const clampedScore = Math.max(0, Math.min(100, score));
  const colorClass = getBarColorClass(clampedScore);

  return (
    <div className="h-1 w-[60px] mx-auto rounded-full bg-muted overflow-hidden">
      <div
        className={`${colorClass} h-full rounded-full transition-all duration-300`}
        style={{ width: `${clampedScore}%` }}
        role="progressbar"
        aria-valuenow={clampedScore}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </div>
  );
}

/**
 * Score display with inline progress bar
 */
function ScoreWithBar({ score }: { score: number }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <ScoreDisplay score={score} />
      <ScoreBar score={score} />
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * CompareTable
 *
 * Renders a comparison table for funds with all metrics and best-value highlighting.
 */
export function CompareTable({ funds }: CompareTableProps) {
  const bestValues = useBestValues(funds);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fund Comparison</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="pb-4 text-left font-medium text-muted-foreground min-w-[150px]">
                Metric
              </th>
              {funds.map((fund) => (
                <th key={fund.isin} className="pb-4 text-center min-w-[180px]">
                  <Link
                    to="/explore/$fundId"
                    params={{ fundId: fund.isin }}
                    className="hover:text-primary transition-colors"
                  >
                    <div className="font-bold text-sm">{fund.amcName}</div>
                    <div className="text-xs font-normal text-muted-foreground mt-1 max-w-[180px] truncate">
                      {fund.name}
                    </div>
                  </Link>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Category Row */}
            <tr className="border-b">
              <td className="py-4 font-medium">Category</td>
              {funds.map((fund) => (
                <td key={fund.isin} className="py-4 text-center">
                  <Badge variant="outline">
                    {CATEGORY_LABELS[fund.category] || fund.category}
                  </Badge>
                </td>
              ))}
            </tr>

            {/* Rating Row */}
            <tr className="border-b">
              <td className="py-4 font-medium">Rating</td>
              {funds.map((fund) => (
                <td key={fund.isin} className="py-4 text-center">
                  <RatingDisplay rating={fund.rating} />
                </td>
              ))}
            </tr>

            {/* Rank Row */}
            <tr className="border-b">
              <td className="py-4 font-medium">Category Rank</td>
              {funds.map((fund) => (
                <td
                  key={fund.isin}
                  className={`py-4 text-center ${fund.rank === bestValues.rank ? "font-bold text-green-600" : ""}`}
                >
                  #{fund.rank} of {fund.totalFundsInCategory}
                </td>
              ))}
            </tr>

            {/* Curie Score Row */}
            <tr className="border-b bg-muted/30">
              <td className="py-4 font-medium">Curie Score</td>
              {funds.map((fund) => {
                const curieScore = calculateCurieScore(fund);
                return (
                  <td
                    key={fund.isin}
                    className={`py-4 text-center ${curieScore === bestValues.curieScore ? "font-bold text-green-600" : ""}`}
                  >
                    <span className="text-lg">{curieScore}</span>
                    <span className="text-sm text-muted-foreground">/100</span>
                  </td>
                );
              })}
            </tr>

            {/* Score Components */}
            <tr className="border-b">
              <td className="py-4 font-medium text-sm pl-4">Consistency</td>
              {funds.map((fund) => (
                <td
                  key={fund.isin}
                  className={`py-4 text-center ${fund.consistencyScore === bestValues.consistencyScore ? "font-bold text-green-600" : ""}`}
                >
                  <ScoreWithBar score={fund.consistencyScore} />
                </td>
              ))}
            </tr>

            <tr className="border-b">
              <td className="py-4 font-medium text-sm pl-4">Recency</td>
              {funds.map((fund) => (
                <td
                  key={fund.isin}
                  className={`py-4 text-center ${fund.recencyScore === bestValues.recencyScore ? "font-bold text-green-600" : ""}`}
                >
                  <ScoreWithBar score={fund.recencyScore} />
                </td>
              ))}
            </tr>

            <tr className="border-b">
              <td className="py-4 font-medium text-sm pl-4">Volatility</td>
              {funds.map((fund) => (
                <td
                  key={fund.isin}
                  className={`py-4 text-center ${fund.volatilityScore === bestValues.volatilityScore ? "font-bold text-green-600" : ""}`}
                >
                  <ScoreWithBar score={fund.volatilityScore} />
                </td>
              ))}
            </tr>

            {/* Dynamic Metrics */}
            {COMPARISON_METRICS.map((metric) => {
              const bestValue = metric.lowerIsBetter
                ? bestValues[metric.key as keyof typeof bestValues]
                : bestValues[metric.key as keyof typeof bestValues];

              return (
                <tr key={metric.key} className="border-b">
                  <td className="py-4 font-medium">{metric.label}</td>
                  {funds.map((fund) => {
                    const value = metric.getValue
                      ? metric.getValue(fund)
                      : (fund[metric.key as keyof Fund] as number);
                    const isBest = value === bestValue;
                    const isReturn = metric.key.includes("return");

                    return (
                      <td
                        key={fund.isin}
                        className={`py-4 text-center ${isBest ? "font-bold text-green-600" : ""}`}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {isReturn && typeof value === "number" && (
                            value >= 0 ? (
                              <TrendingUp className="h-3 w-3 text-green-600" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-red-600" />
                            )
                          )}
                          {metric.format(value)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {/* Exit Load Row */}
            <tr className="border-b">
              <td className="py-4 font-medium">Exit Load</td>
              {funds.map((fund) => (
                <td key={fund.isin} className="py-4 text-center text-sm">
                  {fund.exitLoad || "Nil"}
                </td>
              ))}
            </tr>

            {/* Lock-in Period Row */}
            <tr className="border-b">
              <td className="py-4 font-medium">Lock-in Period</td>
              {funds.map((fund) => (
                <td key={fund.isin} className="py-4 text-center">
                  {fund.lockInPeriod > 0
                    ? `${Math.round(fund.lockInPeriod / 365)} years`
                    : "None"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for CompareTable
 */
export function CompareTableSkeleton({
  columnCount = 3,
  className,
}: {
  columnCount?: number;
  className?: string;
}) {
  const rowCount = 12; // Category, Rating, Rank, Curie Score, 3 score components, 5 metrics rows

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="pb-4 text-left min-w-[150px]">
                <Skeleton className="h-4 w-16" />
              </th>
              {Array.from({ length: columnCount }).map((_, i) => (
                <th key={i} className="pb-4 text-center min-w-[180px]">
                  <div className="flex flex-col items-center gap-1">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowCount }).map((_, rowIdx) => (
              <tr key={rowIdx} className="border-b">
                <td className="py-4">
                  <Skeleton className="h-4 w-24" />
                </td>
                {Array.from({ length: columnCount }).map((_, colIdx) => (
                  <td key={colIdx} className="py-4">
                    <div className="flex flex-col items-center gap-1">
                      <Skeleton className="h-4 w-16" />
                      {rowIdx >= 4 && rowIdx < 7 && (
                        <Skeleton className="h-1 w-[60px] rounded-full" />
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
